import { Fragment, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Button } from 'antd';
import QuestionnaireItem from './questionnaire-item';
import QuestionnaireHeader from './questionnaire-header';
import QuestionnairePartBreak from './questionnaire-part-break';
import styles from '../../style/examinee/questionnaire-test-page.module.css';
import { useQuestionnaireSession } from '../../hooks/useQuestionnaireSession';

const QuestionnaireTestPage = () => {
  const { sessionID } = useParams();
  // The key /session/generate gave this rater; saves need it (PIN-324).
  const raterKey = new URLSearchParams(useLocation().search).get('key') || undefined;
  const [submitted, setSubmitted] = useState(false);

  const {
    items,
    documents,
    responses,
    guesses,
    headerData,
    loading,
    submitting,
    allAnswered,
    hasRespondentName,
    respondentNameRequired,
    resumedRespondentName,
    saveError,
    submitError,
    handleResponseChange,
    handleGuessChange,
    handleUpdateData,
    handleParentNameChange,
    handleSubmit
  } = useQuestionnaireSession(sessionID, {
    respondentNameRequired: true,
    requireTextResponses: true,
    raterKey,
    successMessage: 'Your responses have been recorded.'
  });

  const onSubmitSuccess = () => setSubmitted(true);

  // STARS is rated by parent, teacher, or student — the generic "parent"
  // wording below is wrong for its Teacher/Student forms (PIN-186), so derive
  // the rater noun from the form subtitle ("Teacher Form" -> "teacher") there.
  // Other batteries keep the original hardcoded wording unchanged.
  const isStars = headerData?.title?.includes('STARS');
  const starsRaterNoun = headerData?.subtitle?.match(/^(.*)\s+Form$/i)?.[1]?.toLowerCase();
  const respondentNameWarning =
    isStars && starsRaterNoun
      ? `Please enter the name of the ${starsRaterNoun} completing this form before submitting.`
      : 'Please enter the name of the parent completing this form before submitting.';

  if (loading) {
    return (
      <div className={styles.loading} data-testid="questionnaire-loading">
        Loading questionnaire...
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <h2>Thank you for completing the questionnaire.</h2>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <QuestionnaireHeader
          headerData={headerData}
          onParentNameChange={handleParentNameChange}
          parentNameRequired={respondentNameRequired}
          resumedParentName={resumedRespondentName}
        />

        <div className={styles.itemList} data-testid="questionnaire-item-list">
          {items.map((item, i) => (
            <Fragment key={item._id}>
              <QuestionnairePartBreak
                item={item}
                previousItem={items[i - 1]}
                parts={headerData?.parts}
              />
              <QuestionnaireItem
                index={i + 1}
                item={item}
                document={documents[item._id]}
                responses={responses}
                guesses={guesses}
                allowGuess={headerData?.allowGuess === true}
                onResponseChange={handleResponseChange}
                onGuessChange={handleGuessChange}
                onUpdateData={handleUpdateData}
              />
            </Fragment>
          ))}
        </div>

        <div className={styles.footer}>
          {saveError && (
            <p className={styles.incompleteWarning} data-testid="questionnaire-save-error">
              Some answers could not be saved ({saveError}). They will be sent again when you
              submit.
            </p>
          )}
          {submitError && (
            <p className={styles.incompleteWarning} data-testid="questionnaire-submit-error">
              Your responses could not be submitted: {submitError}
            </p>
          )}
          {headerData && !hasRespondentName && (
            <p className={styles.incompleteWarning} data-testid="questionnaire-warning-name">
              {respondentNameWarning}
            </p>
          )}
          {!allAnswered && (
            <p className={styles.incompleteWarning} data-testid="questionnaire-warning-incomplete">
              There are one or more items that need to be completed before you can submit this form.
            </p>
          )}
          <Button
            type="primary"
            size="large"
            onClick={() => handleSubmit(onSubmitSuccess)}
            loading={submitting}
            disabled={!allAnswered || !hasRespondentName || submitting}
            data-testid="questionnaire-submit"
          >
            Submit Questionnaire
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireTestPage;
